<template>
    <div class="external-propose-time-page">
        <div v-if="!isProposedTimeSent" class="page-header">
            <h1 class="page-title">{{ $t('pages.referrals-pool.index.external_propose_time_page.title') }}</h1>
            <p class="page-subtitle">{{ $t('pages.referrals-pool.index.external_propose_time_page.description') }}</p>
        </div>

        <referrals-confirmation v-if="isProposedTimeSent" heading="Success!" type="success">
            <template #content>
                <common-content-spacer height="medium">
                    <common-page-header
                        :heading="$t('pages.referrals-pool.index.external_propose_time_page.success.heading')"
                        :content="$t('pages.referrals-pool.index.external_propose_time_page.success.content')"
                        size="medium"
                        contentSpacing="medium"
                        :isCenter="true"
                        contentColor="secondary"
                    ></common-page-header>
                </common-content-spacer>
            </template>
        </referrals-confirmation>

        <common-page-split v-else :reverse="true">
            <template #aside>
                <common-content-spacer height="tiny">
                    <common-content-group>
                        <common-content-spacer height="tiny">
                            <common-page-header
                                :heading="$t('pages.referrals-pool.index.external_propose_time_page.selected_time_slots')"
                                size="small"
                                contentSpacing="medium"
                            ></common-page-header>
                            <referral-time-slots-preview
                                :groupSlotsByDate="selectedSlotsByDate"
                                :showDeleteIcon="true"
                                @remove-item="onRemoveItem"
                            ></referral-time-slots-preview>

                            <common-buttons>
                                <common-button
                                    :label="$t('pages.referrals-pool.index.external_propose_time_page.submit_time_slots')"
                                    :isSubmittable="false"
                                    :isDisabled="sharedState.totalSlotCount < 3"
                                    size="medium"
                                    type="primary"
                                    :isProcessing="isSending"
                                    @click="submitTimeSlots()"
                                ></common-button>
                            </common-buttons>
                        </common-content-spacer>
                    </common-content-group>
                </common-content-spacer>
            </template>

            <template #main>
                <common-content-wrapper width="huge">
                    <external-schedule
                        ref="externalSchedule"
                        :physician="externalPhysician"
                        :practice="practice"
                        :selectedSlotsByDate="selectedSlotsByDate"
                        :timeframe="$route.query.timeframe"
                        :createdAt="$route.query.createdAt"
                        @selectSlot="slotChanged($event)"
                    ></external-schedule>
                </common-content-wrapper>
            </template>
        </common-page-split>

        <confirm-slots-modal
            v-if="showConfirmSlotsModal"
            :isOpen="true"
            :selectedSlotsByDate="selectedSlotsByDate"
            :isProcessing="isSending"
            @confirm="handleConfirmSlots"
            @close="closeConfirmSlotsModal"
        ></confirm-slots-modal>

        <portal-target name="modals"></portal-target>
    </div>
</template>

<script>
    import ExternalSchedule from 'components/page-elements/referrals/ExternalSchedule'
    import dayjs from 'dayjs'
    import emitter from 'tiny-emitter/instance'
    import ReferralTimeSlotsPreview from 'components/page-elements/referrals/ReferralTimeSlotsPreview.vue'
    import ProviderDatabaseAPI from 'api/ProviderDatabaseAPI'
    import ConfirmSlotsModal from 'components/page-elements/auth/ConfirmSlotsModal.vue'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import ReferralsConfirmation from 'components/page-elements/ReferralsConfirmation.vue'

    export default {
        components: {
            ExternalSchedule,
            ReferralTimeSlotsPreview,
            ConfirmSlotsModal,
            ReferralsConfirmation,
        },

        data() {
            return {
                externalPhysician: {
                    id: null,
                    firstName: 'External',
                    lastName: 'Provider',
                    scheduleType: 'custom',
                    workingHours: {
                        monday: { start: '08:00', end: '17:00', isWorking: true },
                        tuesday: { start: '08:00', end: '17:00', isWorking: true },
                        wednesday: { start: '08:00', end: '17:00', isWorking: true },
                        thursday: { start: '08:00', end: '17:00', isWorking: true },
                        friday: { start: '08:00', end: '17:00', isWorking: true },
                        saturday: { start: '08:00', end: '17:00', isWorking: false },
                        sunday: { start: '08:00', end: '17:00', isWorking: false },
                    },
                    appointmentLength: 60,
                },
                practice: {},
                selectedSlotsByDate: {},
                sharedState: {
                    totalSlotCount: 0,
                },
                isSending: false,
                isProposedTimeSent: false,
                referralPoolId: null,
                npiNumber: null,
                token: null,
                showConfirmSlotsModal: false,
            }
        },

        provide() {
            return {
                isReferralPool: true,
                sharedState: this.sharedState,
            }
        },

        methods: {
            slotChanged(payload) {
                const date = payload.date
                const updatedSlots = payload.slots.map((slot) => ({
                    label: slot.label,
                    date: slot.value.date,
                    startTime: slot.value.startTime,
                    endTime: slot.value.endTime,
                    from: slot.value.from,
                    to: slot.value.to,
                }))

                updatedSlots.sort((a, b) => {
                    const dateA = dayjs(`${a.date} ${a.startTime}`)
                    const dateB = dayjs(`${b.date} ${b.startTime}`)
                    return dateA - dateB
                })
                this.$set(this.selectedSlotsByDate, date, updatedSlots)
                const sortedSlotsByDate = {}
                Object.keys(this.selectedSlotsByDate)
                    .sort((a, b) => dayjs(a).unix() - dayjs(b).unix())
                    .forEach((key) => {
                        sortedSlotsByDate[key] = this.selectedSlotsByDate[key]
                    })

                this.selectedSlotsByDate = sortedSlotsByDate
                this.updateSlotCount()
            },

            updateSlotCount() {
                this.sharedState.totalSlotCount = Object.values(this.selectedSlotsByDate).reduce(
                    (total, slots) => total + slots.length,
                    0,
                )
            },

            onRemoveItem(payload) {
                this.removeItem(payload.slot, payload.date)
            },

            removeItem(slot, date) {
                const slotsForDate = this.selectedSlotsByDate[date]
                const updatedSlots = slotsForDate.filter((s) => s.label !== slot.label)

                if (updatedSlots.length) {
                    this.$set(this.selectedSlotsByDate, date, updatedSlots)
                } else {
                    this.$delete(this.selectedSlotsByDate, date)
                }

                this.updateSlotCount()
                emitter.emit('slot-removed', { slot: slot, date: date })
            },

            async submitTimeSlots() {
                this.showConfirmSlotsModal = true
            },

            async handleConfirmSlots() {
                const proposedTimeSlots = []
                Object.values(this.selectedSlotsByDate).forEach((slots) => {
                    slots.forEach((slot) => {
                        proposedTimeSlots.push({
                            date: slot.date,
                            startTime: slot.startTime,
                            endTime: slot.endTime,
                        })
                    })
                })

                const request = {
                    referralPoolId: Number(this.referralPoolId),
                    npi: this.npiNumber,
                    token: this.token,
                    proposedTimeSlots: proposedTimeSlots,
                }

                this.isSending = true

                ReferralsPoolAPI.externalProposeTimeSlots(this.referralPoolId, request)
                    .then(() => {
                        this.isProposedTimeSent = true
                    })
                    .finally(() => {
                        this.isSending = false
                        this.showConfirmSlotsModal = false
                    })
            },

            closeConfirmSlotsModal() {
                this.showConfirmSlotsModal = false
            },

            closeWindow() {
                window.close()
            },

            fetchProviderByNpi() {
                const fields = [
                    'firstName',
                    'middleName',
                    'lastName',
                    'email',
                    'phone',
                    'designation',
                    'specialities',
                    'practiceName',
                    'practiceAddress',
                ]

                ProviderDatabaseAPI.get(this.npiNumber, fields, this.referralPoolId).then((res) => {
                    this.externalPhysician = { ...this.externalPhysician, ...res }
                })
            },
        },

        mounted() {
            this.referralPoolId = this.$route.query.referralPoolId
            this.npiNumber = this.$route.query.npiNumber
            this.token = this.$route.query.token

            if (!this.referralPoolId || !this.npiNumber || !this.token) {
                this.$router.push({ name: 'auth.register-token' })
            }
        },
    }
</script>

<style scoped lang="scss">
    .external-propose-time-page {
        min-height: 100vh;
        background: #f8f9fa;
        padding: 40px 20px;
    }

    .page-header {
        text-align: center;
        margin-bottom: 40px;
    }

    .page-title {
        font-size: 32px;
        font-weight: 700;
        color: #212529;
        margin-bottom: 12px;
    }

    .page-subtitle {
        font-size: 16px;
        color: #6c757d;
        margin: 0;
    }
</style>
